import type { SubmissionListParams } from '../types/submission';

/**
 * Serialise a `SubmissionListParams` object into a plain object that
 * Axios's `params` config can consume. Rules:
 *
 *   - Undefined / null / empty-string values are dropped (so an empty
 *     search box doesn't send `?search=`).
 *   - Booleans are stringified (`?summary=true`).
 *   - Everything else passes through as-is; Axios encodes it.
 *
 * Keeping this in a helper — instead of inlining into the hook —
 * means the filter-bar component can normalise its state through the
 * same function that the service uses, so equality checks (React
 * Query cache keys, useEffect deps) roundtrip predictably.
 */
export function toQueryParams(
  params: SubmissionListParams | undefined,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!params) return out;
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null) continue;
    if (typeof raw === 'string' && raw.trim() === '') continue;
    if (typeof raw === 'boolean') {
      out[key] = raw;
      continue;
    }
    if (typeof raw === 'number' || typeof raw === 'string') {
      out[key] = raw;
      continue;
    }
    // Anything else (arrays, objects) — stringify. Backend accepts CSV
    // for multi-value filters in most other places in this codebase.
    if (Array.isArray(raw)) {
      const csv = raw.filter((v) => v !== undefined && v !== null && v !== '').join(',');
      if (csv) out[key] = csv;
      continue;
    }
    // Fallback: JSON so we don't silently drop unknown shapes.
    out[key] = JSON.stringify(raw);
  }
  return out;
}

/**
 * Stable string key for a filter set — used as a React Query cache
 * key ingredient, or (with the current no-React-Query setup) as a
 * useEffect dep to trigger refetch when filters change. Sorts keys
 * alphabetically so `{page:1, search:'x'}` and `{search:'x', page:1}`
 * produce the same key.
 */
export function filterKey(params: SubmissionListParams | undefined): string {
  const normalised = toQueryParams(params);
  const keys = Object.keys(normalised).sort();
  return keys.map((k) => `${k}=${normalised[k]}`).join('&');
}
